import { View, Text, StyleSheet } from '@react-pdf/renderer';
import React, { useRef } from 'react';

const styles = StyleSheet.create({
  container: {
    width: '100%'
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: '#DFE2E7',
    minHeight: 24,
    paddingBottom: 4,
    paddingLeft: 4
  },
  headerText: {
    fontFamily: 'Noto Sans',
    fontSize: 12,
    fontWeight: 400,
    color: '#14151C',
    marginRight: 4
  },
  headerTextContinued: {
    fontSize: 9,
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    color: '#363948',
    textTransform: 'uppercase',
    marginLeft: 4
  },
  content: {
    padding: 4
  },
  contentText: {
    fontSize: 9,
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    color: '#14151C'
  },
  continuedText: {
    fontSize: 9,
    fontFamily: 'Noto Sans',
    fontWeight: 400,
    color: '#4D5165',
    textTransform: 'uppercase',
    textAlign: 'right'
  },
  continuedTextContainer: {
    bottom: -12
  },
  inputRow: {
    width: '100%',
    height: 22,
    borderBottom: '1px solid #DFE2E7'
  }
});

const renderContent = (content: React.ReactNode | string) => {
  if (typeof content === 'string') {
    return (
      <View wrap>
        <Text style={styles.contentText}>{content}</Text>
      </View>
    );
  }
  return content;
};

const renderHeader = (
  headerText: string,
  pageNumber: number,
  previousPageNumber: React.RefObject<number | null>,
  headerContinuedText: string = 'Continued'
) => {
  const isContinuedText =
    previousPageNumber.current !== null &&
    pageNumber > previousPageNumber.current;

  // eslint-disable-next-line no-param-reassign
  previousPageNumber.current = pageNumber;

  return (
    <Text>
      <Text style={styles.headerText}>{headerText}</Text>{' '}
      {isContinuedText ? (
        <Text style={styles.headerTextContinued}>{headerContinuedText}</Text>
      ) : (
        <Text />
      )}
    </Text>
  );
};

interface ContinuedTextState {
  prevPassPages: Set<number>;
  currPassPages: Set<number>;
  lastPageSeen: number;
}

const ContentSection = ({
  headerText,
  content,
  continuedText,
  headerContinuedText,
  hasInputRows = false,
  shouldBreak = false
}: {
  headerText: string;
  content: React.ReactNode | string;
  continuedText: string;
  headerContinuedText: string;
  hasInputRows?: boolean;
  shouldBreak?: boolean;
}) => {
  const previousPageNumber = useRef<number | null>(null);
  const continuedState = useRef<ContinuedTextState>({
    prevPassPages: new Set(),
    currPassPages: new Set(),
    lastPageSeen: 0
  });

  // Detects layout-pass boundaries by watching for pageNumber going backwards.
  // On a new pass, promotes currPassPages → prevPassPages so we can look up
  // whether page N+1 existed in the *previous* pass without oscillating.
  const renderContinuedText = (pageNumber: number) => {
    if (pageNumber < continuedState.current.lastPageSeen) {
      continuedState.current.prevPassPages = new Set(
        continuedState.current.currPassPages
      );
      continuedState.current.currPassPages = new Set();
    }
    continuedState.current.lastPageSeen = pageNumber;
    continuedState.current.currPassPages.add(pageNumber);

    if (continuedState.current.prevPassPages.has(pageNumber + 1)) {
      return <Text style={styles.continuedText}>{continuedText}</Text>;
    }

    return null;
  };

  return (
    <View style={styles.container} break={shouldBreak}>
      <Text
        style={styles.header}
        render={({ pageNumber }) =>
          renderHeader(
            headerText,
            pageNumber,
            previousPageNumber,
            headerContinuedText
          )
        }
        fixed
      />
      {content && (
        <View style={styles.content}>
          {renderContent(content)}
          {continuedText && (
            <Text
              fixed
              style={styles.continuedTextContainer}
              render={({ pageNumber }) => renderContinuedText(pageNumber)}
            />
          )}
        </View>
      )}
      {hasInputRows && (
        <View wrap={false} data-testid="input-rows">
          <View style={styles.inputRow} />
          <View style={styles.inputRow} />
          <View style={styles.inputRow} />
          <View style={styles.inputRow} />
        </View>
      )}
    </View>
  );
};

export default ContentSection;
